import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import { API } from "../../../config";
import axios from "axios";

import {
  addHours,
  addHoursSameTime,
  setZeroHrs,
} from "../dashboard/dashboardAsyncActions";

import { createCerealDictionary } from "../dataTable/DataTableAsyncActions";
import moment from "moment";
import { CerealType } from "../dataTable/DataTableTypes";
import { fetchCropInfo } from "./dynamicCharts";
import {
  setCounOfUserActivityTab,
  setHasDataInCharts,
  setUsersTable,
} from "../../slices/dashboard/dashboard";

export const transformToUsersTableFormat = (allCompleteData: any[]) => {
  return allCompleteData.reduce(
    (acc, action) => {
      const user = action.username;
      const site = action.geoPointName;
      const cereal = action.cropTextIdLocale || "Unknown";

      // Search for existing entry for this user-site combination
      let userSiteEntry = acc.find(
        (entry) => entry.user === user && entry.site === site
      );

      if (!userSiteEntry) {
        // Create new entry for this user-site combination
        userSiteEntry = {
          user: user,
          site: site,
          userId: action.userId,
          totalActions: 0,
          varietyActions: [],
        };
        acc.push(userSiteEntry);
      }

      // Increment the total actions count
      userSiteEntry.totalActions += 1;

      // Search for existing variety in varietyActions
      let varietyEntry = userSiteEntry.varietyActions.find(
        (v) => v.cereal === cereal
      );

      if (!varietyEntry) {
        // Create new entry for this variety
        varietyEntry = {
          cereal: cereal,
          count: 0,
        };
        userSiteEntry.varietyActions.push(varietyEntry);
      }

      // Increment the count for this variety
      varietyEntry.count += 1;

      return acc;
    },
    [] as Array<{
      user: string;
      site: string;
      totalActions: number;
      varietyActions: Array<{
        cereal: string;
        count: number;
      }>;
    }>
  );
};

export const transformToChartActivityFormat = (
  allCompleteData: any[],
  dataDateActivity: Date[],
  locale: string,
  cerealDictionary: Record<number, CerealType>
) => {
  // Use cropTextIdLocale to get all varieties
  const allVarieties = [
    ...new Set(
      allCompleteData.map((action) => action.cropTextIdLocale || "Unknown")
    ),
  ];

  const startDate = new Date(dataDateActivity[0]);
  const endDate = new Date(dataDateActivity[1]);

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  // create structure of data grouped by variety
  const varietiesData: Record<
    string,
    { date: string; count: number; color: string | undefined }[]
  > = {};

  // Initialize all varieties
  for (const variety of allVarieties) {
    varietiesData[variety] = [];
  }

  // Get color mapping from cerealDictionary
  //
  const getColorForVariety = (varietyLocale: string) => {
    const actionWithThisVariety = allCompleteData.find(
      (action) => action.cropTextIdLocale === varietyLocale
    );

    if (actionWithThisVariety?.cropTextId) {
      const varietyEntry =
        cerealDictionary[actionWithThisVariety.cropTextId.id];
      return varietyEntry?.iconBackgroundHexColor || "#cccccc";
    }

    return "#cccccc"; // default color if not found
  };

  // Process each day in the date range -
  const getDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const currentDate = new Date(startDate);
  const endDateString = getDateString(endDate);

  while (getDateString(currentDate) <= endDateString) {
    const dateString = getDateString(currentDate);

    // Count daily counts per variety usando cropTextIdLocale
    const dailyCounts: Record<string, number> = {};
    for (const variety of allVarieties) {
      dailyCounts[variety] = allCompleteData.filter(
        (action) =>
          getDateString(new Date(action.createdAt)) === dateString &&
          (action.cropTextIdLocale || "Unknown") === variety
      ).length;
    }

    // Add daily counts to varietiesData with colors
    for (const variety of allVarieties) {
      varietiesData[variety].push({
        date: dateString,
        count: dailyCounts[variety],
        color: getColorForVariety(variety),
      });
    }

    // Increment date by one day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Construct the final cereals array
  const cerealsArray = Object.entries(varietiesData).map(
    ([specie, countInfo]) => ({
      specie: specie,
      color: getColorForVariety(specie),
      countInfo: countInfo.map((values) => {
        const momentDate = moment(values.date + "T00:00:00").locale(locale);
        const formattedDate = momentDate.format("l");

        return {
          date: formattedDate,
          activity: values.count,
        };
      }),
    })
  );

  return { data: cerealsArray };
};

export const fetchUsers = async (
  token: string,
  locale: string
): Promise<any> => {
  try {
    const result = await axios.get(`${API}/users/user`, {
      headers: {
        "Accept-Language": locale,
        Authorization: `Bearer ${token}`,
      },
    });
    const users = result.data.data.map(
      (user: { id: string; username: string }) => ({
        id: user.id,
        username: user.username,
      })
    );

    return users;
  } catch (error) {
    return error;
  }
};

export const activityChartValues = createAsyncThunk(
  "SharedDataReducer/activityChartValues",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const { cerealInformation } = state.DataTableReducer;
    const { dataDateActivity, locations } = state.DashboardFiltersReducer;
    const { selectedCrop } = state.DashboardReducer;

    const fromDate = `createdAt[$gt]=${addHours(
      setZeroHrs(dataDateActivity[0])
    ).toISOString()}`;

    const toDate = `createdAt[$lt]=${addHoursSameTime(
      setZeroHrs(dataDateActivity[1])
    ).toISOString()}`;

    const filterLocation = locations.valuesSelected
      ?.flatMap((location) => `&geoPointName[$in][]=${location.name}`)
      .join("");

    const queryParams = [`$limit=${100}`, fromDate, toDate]
      .filter(Boolean)
      .join("&");

    try {
      const result = await axios.get(
        `${API}/portal/managed-data-table?userFacingDeletion=false&${queryParams}${
          locations.allValuesSelected === true ? "" : filterLocation
        }`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        }
      );

      if (selectedCrop.slug.length > 0) {
        store.dispatch(fetchCropInfo(selectedCrop.slug));
      }

      const cerealDictionary = await createCerealDictionary(
        cerealInformation,
        store
      );

      const currentPageData = result.data.data;
      const limit = result.data.limit;
      const total = result.data.total;

      store.dispatch(
        setHasDataInCharts({ hasData: total > 0, hasDataBeenLoaded: true })
      );
      let allCompleteData = [...currentPageData];
      let current = limit;

      while (current < total) {
        const subResult = await axios.get(
          `${API}/portal/managed-data-table?userFacingDeletion=false&$skip=${current}&$limit=${limit}&${fromDate}&${toDate}${
            locations.allValuesSelected === true ? "" : filterLocation
          }`,
          {
            headers: {
              "Accept-Language": locale,
              Authorization: `Bearer ${user!.token}`,
            },
          }
        );

        allCompleteData.push(...subResult.data.data);
        current += subResult.data.limit;
      }

      const usersTable = transformToUsersTableFormat(allCompleteData);

      const users = await fetchUsers(user!.token, locale);
      const activeUsers = usersTable.filter(
        (user) => user.totalActions > 0
      ).length;

      const myCountUserObject = {
        totalUsers: users.length,
        countOfUsersActive: activeUsers,
        countOfUsersInactive: users.length - activeUsers,
      };

      store.dispatch(setUsersTable(usersTable));
      store.dispatch(setCounOfUserActivityTab(myCountUserObject));

      const transformedData = transformToChartActivityFormat(
        allCompleteData,
        dataDateActivity,
        locale,
        cerealDictionary
      );

      return transformedData;
    } catch (error) {
      return error;
    }
  }
);
